
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SpinnerIcon, ErrorMessage, ActionButtons } from "./loading";

interface LoadingIndicatorProps { 
  timeout?: number;
  onTimeout?: () => void;
  onRetry?: () => void;
  onGoToProducts?: () => void;
  showActionButtons?: boolean;
  loadingMessage?: string;
}

const LoadingIndicator = ({ 
  timeout = 15000, 
  onTimeout,
  onRetry,
  onGoToProducts,
  showActionButtons = false,
  loadingMessage = "Loading your dashboard..."
}: LoadingIndicatorProps) => {
  const [loadingTime, setLoadingTime] = useState(0);
  const [showRetry, setShowRetry] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const startTime = Date.now();
    let timer: NodeJS.Timeout | undefined;
    
    // If not already showing buttons, set timeout for retry option
    if (!showActionButtons) {
      timer = setTimeout(() => {
        setShowRetry(true);
        if (onTimeout) {
          onTimeout();
        }
      }, timeout);
    } else {
      // If buttons are already showing, set showRetry to true immediately
      setShowRetry(true);
    }
    
    // Update loading time every second
    const interval = setInterval(() => {
      setLoadingTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    
    return () => {
      if (timer) clearTimeout(timer);
      clearInterval(interval);
    };
  }, [timeout, onTimeout, showActionButtons]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoBack = () => {
    if (onGoToProducts) {
      onGoToProducts();
    } else {
      navigate("/products");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md mx-auto">
        <SpinnerIcon className="mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">{loadingMessage}</h2>
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          ({loadingTime}s)
        </p>
        
        {(showRetry || showActionButtons) && (
          <div className="mt-6 space-y-4">
            <ErrorMessage />
            <ActionButtons 
              onRetry={handleRetry}
              onCancel={handleGoBack}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingIndicator;
